import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { gql, useMutation } from '@apollo/client';
import { useUserAuth } from '../../providers/auth';
import { useVenues } from '../../providers/venues';
import {
  UnreadLatestVenueEventMessagesMutation,
  UnreadLatestVenueEventMessagesMutationVariables,
  ReadAllVenueEventMessagesMutation,
  ReadAllVenueEventMessagesMutationVariables,
  ReadManyEventMessagesMutation,
  ReadManyEventMessagesMutationVariables,
  UnreadManyEventMessagesMutation,
  UnreadManyEventMessagesMutationVariables,
} from '@pv/common/graphql/operations';
import {
  markAllInboxEventsReadInCache,
  markAllInboxEventsUnreadInCache,
  markInboxEventsReadInCache,
  markInboxEventsUnreadInCache,
} from './updateInboxUnreadCache';

const unreadLatestByVenueMutation = gql`
  mutation UnreadLatestVenueEventMessages(
    $input: UnreadLatestVenueEventMessagesInput!
  ) {
    unreadLatestVenueEventMessages(input: $input) {
      errors {
        message
      }
    }
  }
`;

const readAllByVenueMutation = gql`
  mutation ReadAllVenueEventMessages($input: ReadAllVenueEventMessagesInput!) {
    readAllVenueEventMessages(input: $input) {
      errors {
        message
      }
    }
  }
`;

const readAllMutation = gql`
  mutation ReadManyEventMessages($input: ReadAllEventMessagesInput!) {
    readAllEventMessages(input: $input) {
      events {
        id
      }
      errors {
        message
      }
    }
  }
`;

const unreadLatestMutation = gql`
  mutation UnreadManyEventMessages($input: UnreadLatestEventMessagesInput!) {
    unreadLatestEventMessages(input: $input) {
      events {
        id
      }
      errors {
        message
      }
    }
  }
`;

const archiveEventsMutation = gql`
  mutation ArchiveEvents($input: ArchiveEventsInput!) {
    archiveEvents(input: $input) {
      events {
        id
      }
      errors {
        message
      }
    }
  }
`;

const unarchiveEventsMutation = gql`
  mutation UnarchiveEvents($input: UnarchiveEventsInput!) {
    unarchiveEvents(input: $input) {
      events {
        id
      }
      errors {
        message
      }
    }
  }
`;

const archiveVenueEventsMutation = gql`
  mutation ArchiveVenueEvents($input: ArchiveVenueEventsInput!) {
    archiveVenueEvents(input: $input) {
      errors {
        message
      }
    }
  }
`;

const unarchiveVenueEventsMutation = gql`
  mutation UnarchiveVenueEvents($input: UnarchiveVenueEventsInput!) {
    unarchiveVenueEvents(input: $input) {
      errors {
        message
      }
    }
  }
`;

const deleteMessagesMutation = gql`
  mutation DeleteMessages($input: DeleteMessagesInput!) {
    deleteMessages(input: $input) {
      deletedIds
      errors {
        message
      }
    }
  }
`;

export type InboxTab =
  | 'new'
  | 'drafts'
  | 'scheduled'
  | 'unsent'
  | 'archived'
  | 'teamMessages';

const TAB_TITLES: Record<InboxTab, string> = {
  new: 'New',
  drafts: 'Drafts',
  scheduled: 'Scheduled',
  unsent: 'Pending',
  archived: 'Archived',
  teamMessages: 'Team Messages',
};

export const InboxToolbar = ({
  activeTab,
  eventCount = 0,
  unreadEventCount = 0,
  draftCount = 0,
  scheduledCount = 0,
  pendingCount = 0,
  mentionCount = 0,
  unreadMentionCount = 0,
  draftIds = [],
  selected,
  allSelected,
  ownerIds,
  setOwnerIds,
  showUnreadOnly,
  setShowUnreadOnly,
  premiumFeaturesEnabled,
  onArchiveComplete,
}: {
  activeTab: InboxTab;
  eventCount?: number;
  unreadEventCount?: number;
  draftCount?: number;
  scheduledCount?: number;
  pendingCount?: number;
  mentionCount?: number;
  unreadMentionCount?: number;
  draftIds?: Array<string | number>;
  selected: Record<string | number, boolean>;
  allSelected: boolean;
  ownerIds?: string[];
  setOwnerIds: (ownerIds: string[] | undefined) => void;
  showUnreadOnly: boolean;
  setShowUnreadOnly: (val: boolean) => void;
  premiumFeaturesEnabled: boolean;
  onArchiveComplete?: () => void;
}) => {
  const theme = useTheme();
  const largeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const { venueIds } = useVenues();
  const { user } = useUserAuth();

  const isEventTab = activeTab === 'new' || activeTab === 'archived';
  const isTeamMessagesTab = activeTab === 'teamMessages';
  const isDraftsTab = activeTab === 'drafts';
  const showArchived = activeTab === 'archived';

  const [readAllEventMessages] = useMutation<
    ReadManyEventMessagesMutation,
    ReadManyEventMessagesMutationVariables
  >(readAllMutation, {
    update(cache, { data }) {
      if (!data || !data.readAllEventMessages) return;
      const eventIds = data.readAllEventMessages.events.map((event) => event.id);
      markInboxEventsReadInCache(cache, eventIds);
    },
  });

  const [unreadLatestEventMessages] = useMutation<
    UnreadManyEventMessagesMutation,
    UnreadManyEventMessagesMutationVariables
  >(unreadLatestMutation, {
    update(cache, { data }) {
      if (!data || !data.unreadLatestEventMessages) return;
      const eventIds = data.unreadLatestEventMessages.events.map(
        (event) => event.id
      );
      markInboxEventsUnreadInCache(cache, eventIds);
    },
  });

  const [readAllVenueEventMessages] = useMutation<
    ReadAllVenueEventMessagesMutation,
    ReadAllVenueEventMessagesMutationVariables
  >(readAllByVenueMutation, {
    update(cache) {
      markAllInboxEventsReadInCache(cache);
    },
  });

  const [unreadLatestVenueEventMessages] = useMutation<
    UnreadLatestVenueEventMessagesMutation,
    UnreadLatestVenueEventMessagesMutationVariables
  >(unreadLatestByVenueMutation, {
    update(cache) {
      markAllInboxEventsUnreadInCache(cache);
    },
  });

  const [archiveEvents] = useMutation(archiveEventsMutation);
  const [unarchiveEvents] = useMutation(unarchiveEventsMutation);
  const [archiveVenueEvents] = useMutation(archiveVenueEventsMutation);
  const [unarchiveVenueEvents] = useMutation(unarchiveVenueEventsMutation);
  const [deleteMessages, { loading: deleteMessagesLoading }] = useMutation(
    deleteMessagesMutation
  );

  const markAsUnread = useCallback(() => {
    if (allSelected) {
      unreadLatestVenueEventMessages({
        variables: { input: { venueIds, exclude: Object.keys(selected) } },
      });
    } else {
      unreadLatestEventMessages({
        variables: { input: { ids: Object.keys(selected) } },
      });
    }
  }, [
    selected,
    unreadLatestEventMessages,
    allSelected,
    unreadLatestVenueEventMessages,
    venueIds,
  ]);

  const markAsRead = useCallback(() => {
    if (allSelected) {
      readAllVenueEventMessages({
        variables: { input: { venueIds, exclude: Object.keys(selected) } },
      });
    } else {
      readAllEventMessages({
        variables: { input: { ids: Object.keys(selected) } },
      });
    }
  }, [
    selected,
    readAllEventMessages,
    allSelected,
    readAllVenueEventMessages,
    venueIds,
  ]);

  const archiveSelected = useCallback(async () => {
    if (allSelected) {
      await archiveVenueEvents({
        variables: { input: { venueIds, exclude: Object.keys(selected) } },
      });
    } else {
      await archiveEvents({
        variables: { input: { ids: Object.keys(selected) } },
      });
    }
    onArchiveComplete?.();
  }, [
    allSelected,
    archiveVenueEvents,
    archiveEvents,
    venueIds,
    selected,
    onArchiveComplete,
  ]);

  const unarchiveSelected = useCallback(async () => {
    if (allSelected) {
      await unarchiveVenueEvents({
        variables: { input: { venueIds, exclude: Object.keys(selected) } },
      });
    } else {
      await unarchiveEvents({
        variables: { input: { ids: Object.keys(selected) } },
      });
    }
    onArchiveComplete?.();
  }, [
    allSelected,
    unarchiveVenueEvents,
    unarchiveEvents,
    venueIds,
    selected,
    onArchiveComplete,
  ]);

  const selectedDraftIds = useMemo(() => {
    if (!isDraftsTab) {
      return [];
    }
    if (allSelected) {
      return draftIds.filter((id) => !selected[id]);
    }
    return Object.keys(selected);
  }, [allSelected, draftIds, isDraftsTab, selected]);

  const discardSelectedDrafts = useCallback(async () => {
    const count = selectedDraftIds.length;
    if (count === 0) {
      return;
    }
    const label = count === 1 ? 'draft' : 'drafts';
    if (
      !window.confirm(
        `Discard ${count} ${label}? This cannot be undone.`
      )
    ) {
      return;
    }

    await deleteMessages({
      variables: { input: { ids: selectedDraftIds } },
    });
    onArchiveComplete?.();
  }, [deleteMessages, onArchiveComplete, selectedDraftIds]);

  const totalSelected = useMemo(() => {
    if (isDraftsTab) {
      return selectedDraftIds.length;
    }
    if (!allSelected) {
      return Object.keys(selected).length;
    } else {
      return eventCount - Object.keys(selected).length;
    }
  }, [
    allSelected,
    eventCount,
    isDraftsTab,
    selected,
    selectedDraftIds.length,
  ]);

  const eventCopy = eventCount === 1 ? 'event' : 'events';
  const draftCopy = draftCount === 1 ? 'draft' : 'drafts';
  const scheduledCopy =
    scheduledCount === 1 ? 'scheduled message' : 'scheduled messages';
  const pendingCopy =
    pendingCount === 1 ? 'pending message' : 'pending messages';
  const mentionCopy = mentionCount === 1 ? 'message' : 'messages';
  const selectedDraftCopy = totalSelected === 1 ? 'draft' : 'drafts';

  let subtitle;
  if (isEventTab) {
    subtitle = `${unreadEventCount} unread, ${eventCount} ${eventCopy}`;
  } else if (activeTab === 'scheduled') {
    subtitle = `${scheduledCount} ${scheduledCopy}`;
  } else if (activeTab === 'unsent') {
    subtitle = `${pendingCount} ${pendingCopy}`;
  } else if (activeTab === 'teamMessages') {
    subtitle = `${unreadMentionCount} unread, ${mentionCount} ${mentionCopy}`;
  } else {
    subtitle = `${draftCount} ${draftCopy}`;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '20px 24px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        height: largeScreen ? '74px' : 'auto',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: largeScreen ? 'center' : 'left',
          gap: largeScreen ? '18px' : 0,
          flexDirection: largeScreen ? 'row' : 'column',
        }}
      >
        <Typography variant="h5">{TAB_TITLES[activeTab]}</Typography>
        <Typography variant="body1" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>

      {isEventTab && largeScreen && totalSelected > 0 && (
        <Box sx={{ display: 'flex', gap: '18px', alignItems: 'center' }}>
          <Typography variant="body1">
            {totalSelected} {eventCopy} selected
          </Typography>
          {showArchived ? (
            <Button
              disabled={totalSelected === 0}
              variant="outlined"
              color="error"
              onClick={unarchiveSelected}
            >
              Unarchive
            </Button>
          ) : (
            <>
              <Button
                disabled={totalSelected === 0}
                variant="outlined"
                color="error"
                onClick={archiveSelected}
              >
                Archive
              </Button>
              <Button
                disabled={totalSelected === 0}
                variant="outlined"
                color="error"
                onClick={markAsUnread}
              >
                Mark as Unread
              </Button>
              <Button
                disabled={totalSelected === 0}
                variant="outlined"
                color="error"
                onClick={markAsRead}
              >
                Mark as Read
              </Button>
            </>
          )}
        </Box>
      )}

      {isDraftsTab && largeScreen && totalSelected > 0 && (
        <Box sx={{ display: 'flex', gap: '18px', alignItems: 'center' }}>
          <Typography variant="body1">
            {totalSelected} {selectedDraftCopy} selected
          </Typography>
          <Button
            disabled={totalSelected === 0 || deleteMessagesLoading}
            variant="outlined"
            color="error"
            onClick={discardSelectedDrafts}
          >
            Discard
          </Button>
        </Box>
      )}

      {(isEventTab || isTeamMessagesTab) && totalSelected === 0 && user && (
        <Box
          sx={{
            display: 'flex',
            gap: largeScreen ? '18px' : '12px',
            alignItems: largeScreen ? 'center' : 'start',
          }}
        >
          {/* Mentions have no event owner to filter on, so this stays on the
              event tabs. Hiding read rows applies to both. */}
          {isEventTab && premiumFeaturesEnabled && (
            <Button
              variant="outlined"
              color="error"
              size={largeScreen ? 'medium' : 'small'}
              onClick={() => {
                ownerIds ? setOwnerIds(undefined) : setOwnerIds([user.id]);
              }}
            >
              {ownerIds ? 'All Events' : 'My Events'}
            </Button>
          )}
          <Button
            variant="outlined"
            color="error"
            size={largeScreen ? 'medium' : 'small'}
            onClick={() => setShowUnreadOnly(!showUnreadOnly)}
          >
            {showUnreadOnly ? 'Show Read' : 'Hide Read'}
          </Button>
        </Box>
      )}
    </Box>
  );
};
